import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  apiRemoveMemberWallet,
  apiGetWallet,
  apiNewMemberWallet,
} from "../../api/backend/wallet";
import BillingSettingForm from "../../components/forms/billingUpdate/billingUpdate";
import { ImgTopBtn, SolidColorButton } from "../../components/buttons";
import { CreditCardWithLockIcon, LeftArrow } from "../../components/icons";
import { getPaymentIcon } from "../../utils/paymentIcons";
import { useAuth } from "../../components/context/authContext";
import { billingErrorMessages } from "../../constants/errorMessages";
import { getStorageKey } from "../../utils/localStorageStateUniqueKeyGenerator";
import ErrorMessageModal from "../../components/errorMessageModal/errorMessageModal";
import { getCookie, clearCookie, setCookie, clearCurrentContextTypeCookies } from "../../utils/cookies";
import { computeChecksum } from "../../utils/computeChecksum";
import Loader from "../../components/loader/loader";
import "./walletPage.scss";

const WalletPage = () => {
  // passed through main and will be used to check to make sure the current user matches source id
  const { sourceId } = useParams();
  // currentMemberId is used to compare source id for extra added layer of security
  const { currentMemberId } = useAuth();
  // used for backend response will return only one object
  const [wallet, setWallet] = useState(null);
  const [updating, setUpdating] = useState(false);
  const navigate = useNavigate();

  // string state data used with local storage and update or create state
  // currentContextType is used for update or create state
  const [currentContextType, setCurrentContextType] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [fieldErrorMessage, setFieldErrorMessage] = useState("");
  const [errorMessageModal, setErrorModalMessage] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [expirationMonth, setExpirationMonth] = useState("");
  const [expirationYear, setExpirationYear] = useState("");
  const [creditCardNumber, setCreditCardNumber] = useState("");
  const [billingDetails, setBillingDetails] = useState({
    firstName: "",
    lastName: "",
    address1: "",
    address2: "",
    city: "",
    postalCode: "",
    country: "",
  });

  // boleans state data used for flags
  const [isPaymentMethodOpen, setPaymentMethodOpen] = useState(false);
  const [isMonthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const [isYearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [isStateDropdownOpen, setStateDropdownOpen] = useState(false);
  const resetYearDropdown = () => setYearDropdownOpen(false);
  const onYearDropdownReset = () => setYearDropdownOpen(false);
  // Collapsible section logic for card and billing fields
  const [showCardSection, setShowCardSection] = useState(false);
  const [showBillingSection, setShowBillingSection] = useState(false);
  const [loading, setLoading] = useState(false);

  // Example context type string for localStorage keys
  // Context: if a card exists, we're updating; if not, we're creating a new card
  // Fetch the wallet details on mount
  useEffect(() => {
    setLoading(true);
    const fetchWallet = async () => {
      try {
        const response = await apiGetWallet(sourceId);
        // We expect response.data to be:
        // { success: true, wallet: { paymentMethod, creditCardNumber } }
        // or { success: true, wallet: null, message: "No wallet on file." }
        if (response.data.success) {
          // Set wallet to the returned wallet object (which could be null)
          // If no wallet is found, default to an empty object
          setWallet(response.data.wallet); // This can be null if no wallet exists.
        } else {
          // If there's an error property in the response, handle it accordingly
          console.error("Error in response:", response.data.error);
        }
      } catch (error) {
        console.error("Error fetching wallet:", error);
      } finally {
        setLoading(false);
      }
    };

    if (sourceId) {
      fetchWallet();
    }
  }, [sourceId]);

  useEffect(() => {
    if (wallet && wallet.sourceId === currentMemberId && wallet.creditCardNumber) {
      setCurrentContextType("update");
      setUpdating(true);
    } else {
      setCurrentContextType("create");
      setUpdating(false);
    }
  }, [wallet, currentMemberId]);
  useEffect(() => {
    const storedBilling = getCookie(
      getStorageKey("BillingSection", currentContextType)
    );
    if (storedBilling === "true") {
      setShowBillingSection(true);
    } else {
      setShowBillingSection(false);
    }
  }, [currentContextType]);

  const reFetchWallet = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiGetWallet(currentMemberId);
      if (response.data.success) {
        setWallet(response.data.wallet);
      } else {
        console.error("Error in response:", response.data.error);
      }
    } catch (error) {
      console.error("Error fetching wallet:", error);
    } finally {
      setLoading(false);
    }
  }, [currentMemberId, setWallet, setLoading]);
  

  const handleMemberWallet = async () => {
    // Retrieve values from cookies using keys that match your watchers and DB columns.
    const storedValues = {
      paymentMethod:
        getCookie(getStorageKey("paymentMethod", currentContextType)) || "",
      expirationMonth:
        getCookie(getStorageKey("ccExpMonth", currentContextType)) || "",
      expirationYear:
        getCookie(getStorageKey("ccExpYear", currentContextType)) || "",
      creditCardNumber:
        getCookie(getStorageKey("creditCardNumber", currentContextType)) || "",
      billingFirstName:
        getCookie(getStorageKey("billingFirstName", currentContextType)) || "",
      billingLastName:
        getCookie(getStorageKey("billingLastName", currentContextType)) || "",
      billingAddressLine1:
        getCookie(getStorageKey("billingAddressLine1", currentContextType)) ||
        "",
      billingAddressLine2:
        getCookie(getStorageKey("billingAddressLine2", currentContextType)) ||
        "",
      billingCity:
        getCookie(getStorageKey("billingCity", currentContextType)) || "",
      billingState:
        getCookie(getStorageKey("billingState", currentContextType)) || "",
      billingCountry:
        getCookie(getStorageKey("billingCountry", currentContextType)) || "",
      billingZipCode:
        getCookie(getStorageKey("billingZipCode", currentContextType)) || "",
    };

    console.log("Stored Values:", storedValues);

    let hasError = false;
    let newErrors = {};

    // Data validation for "create" mode:
    if (currentContextType === "create") {
      // For creation, open both sections
      setShowCardSection(true);
      setShowBillingSection(true);
      console.log("Creating new wallet details; sections opened.");

      let anyFieldFilled = false;
      Object.keys(storedValues).forEach((key) => {
        if (!storedValues[key].trim()) {
          newErrors[key] = billingErrorMessages[key];
        } else {
          anyFieldFilled = true;
        }
      });
      console.log("New Errors for create:", newErrors);
      console.log("Any Field Filled:", anyFieldFilled);
      setFieldErrorMessage(newErrors);

      if (!anyFieldFilled) {
        setErrorModalMessage(
          "You must fill both sections: Billing and Credit Card."
        );
        console.log("Popup error: No fields filled.");
        return;
      } else {
        setErrorModalMessage("");
      }
    } else if (
      currentContextType === "update" &&
      (showCardSection || showBillingSection)
    ) {
      // For update, only validate the fields for the sections that are open.
      console.log("Updating wallet details; validating open sections.");
      const belongsToCard = [
        "paymentMethod",
        "expirationMonth",
        "expirationYear",
        "creditCardNumber",
      ];
      const belongsToBilling = [
        "billingFirstName",
        "billingLastName",
        "billingAddressLine1",
        "billingCity",
        "billingState",
        "billingCountry",
        "billingZipCode",
      ];

      Object.keys(storedValues).forEach((key) => {
        if (
          showCardSection &&
          belongsToCard.includes(key) &&
          !storedValues[key].trim()
        ) {
          newErrors[key] =
            billingErrorMessages[key] || "This field is required.";
          hasError = true;
          console.log(`Field ${key} is empty in Card Section.`);
        }
        if (
          showBillingSection &&
          belongsToBilling.includes(key) &&
          !storedValues[key].trim()
        ) {
          newErrors[key] =
            billingErrorMessages[key] || "This field is required.";
          hasError = true;
          console.log(`Field ${key} is empty in Billing Section.`);
        }
      });
      console.log("New Errors for update:", newErrors);
      setFieldErrorMessage(newErrors);

      if (hasError) {
        setErrorModalMessage(
          "Please fill out all required fields in the open sections."
        );
        console.log("Popup error: Some open fields are empty.");
        return;
      } else {
        setErrorModalMessage("");
      }
    }
    console.log("Wallet process can proceed.");
    // Now compute the final checksum using the aggregated storedValues.
    // This ensures that you compute a checksum over the entire final object.
    const finalChecksum = computeChecksum(storedValues);
    setCookie(`${currentContextType}_liveChecksum`, finalChecksum, 1);
    console.log("Final computed checksum:", finalChecksum);
    setLoading(true);
    try {
      const sourceId = currentMemberId;
      // Pass storedValues along with the finalChecksum to your API call
      const response = await apiNewMemberWallet(
        sourceId,
        storedValues,
        finalChecksum
      );
      console.log("API Response:", response);
      // Compare server-returned checksum with the computed finalChecksum
      if (response.data.checksum === finalChecksum) {
        // Clear the cookie for live checksum, if needed.
        clearCookie(getStorageKey("liveChecksum", currentContextType));
        clearCurrentContextTypeCookies(currentContextType);
        // Clear UI state
        setPaymentMethod("");
        setExpirationMonth("");
        setExpirationYear("");
        setCreditCardNumber("");
        setBillingDetails({
          billingFirstName: "",
          billingLastName: "",
          billingAddressLine1: "",
          billingAddressLine2: "",
          billingCity: "",
          billingZipCode: "",
          billingCountry: "",
        });
        setSelectedState("");
        setShowCardSection(false);
        setShowBillingSection(false);
        setFieldErrorMessage({});
        setErrorModalMessage("");
      } 
      else {
        console.error("Checksum mismatch. Data integrity compromised.");
      }
      await reFetchWallet();
    } catch (error) {
      console.error("Error updating wallet:", error);
      setErrorModalMessage("Failed to update wallet. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveWallet = async () => {
    try {
      setLoading(true);
      const response = await apiRemoveMemberWallet(currentMemberId);
      console.log("Remove wallet response:", response);
      if (response.data.message) {
        // On success, update the state to indicate no wallet exists.
        setWallet(null);
        setCurrentContextType("create"); // Switch to create mode if needed.
      } else {
        setErrorModalMessage(response.data.error || "Failed to remove wallet");
      }
    } catch (error) {
      console.error("Error removing wallet:", error);
      setErrorModalMessage("Error removing wallet. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="walletPageTop" className="walletPageContainer">
      <SolidColorButton
        solidColorBtnImage={
          <LeftArrow
            height="15px"
            width="15px"
            fillColor="#964b00"
            strokeWidth="4"
            strokeColor="#964b00"
          />
        }
        className="gold-btn"
        solidColorBtnText="Back"
        solidColorBtnFontWeight="800"
        solidColorBtnFontFamily="helvetica"
        solidColorBtnTextFontSize=".75rem"
        solidColorBtnFontColor="#964b00"
        solidColorBtnWidth="100px"
        solidColorBtnBoxShadow="0 4px 3px 1px #000000bb, 0 6px 8px #000000bb, 0 -4px 4px #000000bb, 0 -6px 4px #000000bb, inset 0 0 3px 0 #000000bb"
        onClick={() => navigate(-1)}
      />
      {wallet ? (
        <div className="wallet-details">
          <div className="wallet-header">
            <div className="payment-method-row remove-card-btn">
              {currentContextType === "update" && (
                <>
                  {getPaymentIcon(wallet.paymentMethod)}
                  <span>{`**** ${wallet.creditCardNumber}`}</span>
                </>
              )}
            </div>
            {currentContextType === "update" && (
              <ImgTopBtn
                image={
                  <CreditCardWithLockIcon cardHeight="12px" cardWidth="12px" />
                }
                onClick={handleRemoveWallet} // Attach the handler here.
                imgTopBtnText="Remove Card"
                imgTopBtnTextFontSize="1rem"
                imgTopBtnFontColor="var(--color-bloodred)"
                className="remove_btn"
                paddingRight="10px"
                paddingLeft="10px"
              />
            )}
          </div>
          {/* <p>
            <strong>Expiration Date:</strong> {wallet.expiredCCDate}
          </p> */}
        </div>
      ) : (
        <p>Wallet not found.</p>
      )}
      {errorMessageModal && (
        <ErrorMessageModal
          message={errorMessageModal}
          onClose={() => setErrorModalMessage("")} // Close modal when clicked
        />
      )}
      {loading && <Loader />}
      <BillingSettingForm
        billingDetails={billingDetails}
        setBillingDetails={setBillingDetails}
        fieldErrorMessage={fieldErrorMessage}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        isPaymentMethodOpen={isPaymentMethodOpen}
        setPaymentMethodOpen={setPaymentMethodOpen}
        isMonthDropdownOpen={isMonthDropdownOpen}
        setMonthDropdownOpen={setMonthDropdownOpen}
        expirationMonth={expirationMonth}
        setExpirationMonth={setExpirationMonth}
        expirationYear={expirationYear}
        setExpirationYear={setExpirationYear}
        resetYearDropdown={resetYearDropdown}
        onYearDropdownReset={onYearDropdownReset}
        creditCardNumber={creditCardNumber}
        setCreditCardNumber={setCreditCardNumber}
        selectedState={selectedState}
        setSelectedState={setSelectedState}
        isYearDropdownOpen={isYearDropdownOpen}
        setYearDropdownOpen={setYearDropdownOpen}
        isStateDropdownOpen={isStateDropdownOpen}
        setStateDropdownOpen={setStateDropdownOpen}
        currentContextType={currentContextType}
        updating={updating}
        showCardSection={showCardSection}
        setShowCardSection={setShowCardSection}
        showBillingSection={showBillingSection}
        setShowBillingSection={setShowBillingSection}
        setCurrentContextType={setCurrentContextType}
        handleMemberWallet={handleMemberWallet}
      />
    </div>
  );
};

export default WalletPage;
